import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { PageSettings } from "../../config/page-settings";
import WrapperComponent from "../wrapperComponent";
import PayByLinkForm from "./PayByLinkForm";
import publicApi from "./publicApi";

/**
 * The page a tenant lands on from a payment link Ania emailed them. No login.
 *
 * Backed by two public endpoints from webappbackend PR #355:
 *
 *   GET  /pay/:token/context   200 { data: { suggestedAmount, feeRatePercent,
 *                                           expiresAt, attemptsRemaining,
 *                                           suggestedFirstName, suggestedLastName } }
 *                              404 { error }   — one message for unknown, expired,
 *                                                already-paid and locked alike, so
 *                                                the route cannot be used to probe
 *                                                for valid tokens
 *   POST /pay/:token/capture   200 { data: { response_text, paymentZzID } }
 *                              402 { data: { response_text, attemptsRemaining,
 *                                            locked }, message }
 *                              404 { error }
 *
 * The lease, the person and the receipt email are all resolved from the token on the
 * server and are never accepted from this page — that is the whole point of the token
 * being the credential. This page submits only what a card terminal would: an amount,
 * the cardholder's name and billing address, and the instrument itself.
 *
 * The decline path is what makes this different from the portal's payment modal.
 * There, a decline closes the modal and raises a generic alert, losing everything
 * the tenant typed. Here a decline keeps them on the page with the amount, name and
 * billing address intact, clears only the card or account numbers, and says how many
 * attempts are left before the link closes.
 */

/**
 * The office number shown on every "call us" screen here.
 *
 * Defaulted rather than left blank: these screens exist precisely because
 * something went wrong, and one without a phone number is useless. The number is
 * already hardcoded in the backend's tenant email templates (src/ejs), so this is
 * not a new place to keep it in sync — it is the same fact, already duplicated.
 * REACT_APP_OFFICE_PHONE still overrides it.
 */
const OFFICE_PHONE = process.env.REACT_APP_OFFICE_PHONE || "(973) 523-7690";
const OFFICE_PHONE_DIAL = `tel:${OFFICE_PHONE.replace(/[^0-9+]/g, "")}`;

/** Rendered whenever the link itself is no good, using the server's own wording. */
const InvalidLink = ({ message }) => {
  const { t } = useTranslation();
  return (
    <div className="text-center" style={{ padding: "40px 16px" }}>
      <i className="fa fa-link-slash" style={{ fontSize: "32px", color: "#6c757d" }} />
      <h5 style={{ marginTop: "14px", fontWeight: 600 }}>
        {t("paylink.invalid-title", "This link can no longer be used")}
      </h5>
      <p style={{ color: "#6c757d", fontSize: "14px", maxWidth: "380px", margin: "10px auto 0" }}>
        {message}
      </p>
      {OFFICE_PHONE && (
        <p style={{ marginTop: "12px", fontSize: "14px" }}>
          <a href={OFFICE_PHONE_DIAL}>{OFFICE_PHONE}</a>
        </p>
      )}
    </div>
  );
};

const PayByLink = () => {
  const { token } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const context = useContext(PageSettings);

  const [loading, setLoading] = useState(true);
  const [linkContext, setLinkContext] = useState(null);
  const [invalidMessage, setInvalidMessage] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [decline, setDecline] = useState(null); // { text, attemptsRemaining }
  const [locked, setLocked] = useState(false);
  const [approved, setApproved] = useState(null); // { text, paymentZzID, total }
  const [unresolved, setUnresolved] = useState(false);
  const [secretsResetSignal, setSecretsResetSignal] = useState(0);

  // No portal chrome: this tenant has no session, so a sidebar of portal
  // navigation and a "log out" header would be nonsense. Same approach the other
  // public token pages take (see pages/OnBoardTenant).
  useEffect(() => {
    context.handleSetPageSidebar(false);
    context.handleSetPageHeader(false);
    return () => {
      context.handleSetPageSidebar(true);
      context.handleSetPageHeader(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ania locks to the caller's language for the whole call, so the emailed link
  // can carry ?lang=es and the page opens in the language they actually spoke.
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("lang");
    if (requested === "es" || requested === "en") {
      i18n.changeLanguage(requested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    document.title = "Ania Management | Pay Rent";

    publicApi
      .get(`/pay/${encodeURIComponent(token)}/context`)
      .then(({ data }) => {
        if (cancelled) return;
        setLinkContext(data?.data || null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setInvalidMessage(
          err?.response?.data?.error ||
            t(
              "paylink.generic-error",
              "We could not open this payment link. Please call our office and we'll be happy to help."
            )
        );
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = useCallback(
    (payload) => {
      if (submitting) return; // a double-tap must never become two authorisations
      setSubmitting(true);
      setDecline(null);

      publicApi
        .post(`/pay/${encodeURIComponent(token)}/capture`, payload)
        .then(({ data }) => {
          setApproved({
            text: data?.data?.response_text || "",
            paymentZzID: data?.data?.paymentZzID || "",
            amount: payload.amount,
          });
          setSubmitting(false);
        })
        .catch((err) => {
          const status = err?.response?.status;
          const body = err?.response?.data;

          if (status === 402) {
            // Declined. The server is authoritative on how many tries are left.
            setDecline({
              text: body?.data?.response_text || body?.message || "",
              attemptsRemaining: body?.data?.attemptsRemaining ?? 0,
            });
            setLocked(Boolean(body?.data?.locked));
            setSecretsResetSignal((n) => n + 1);
            setSubmitting(false);
            return;
          }

          if (status === 404) {
            setInvalidMessage(body?.error || "");
            setSubmitting(false);
            return;
          }

          if (status === 400) {
            // Should not happen — this form validates the same rules the backend
            // does — so surface it rather than silently swallowing it.
            setDecline({
              text: t(
                "paylink.rejected",
                "Some of the details entered were not accepted. Please check them and try again."
              ),
              attemptsRemaining: linkContext?.attemptsRemaining ?? null,
            });
            setSubmitting(false);
            return;
          }

          // Timeout, network drop, or a 5xx. We genuinely do not know whether the
          // card was charged, so we must not invite another attempt — that is how
          // a tenant ends up paying twice. Send them to a human instead.
          setUnresolved(true);
          setSubmitting(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, submitting, linkContext]
  );

  if (loading) {
    return (
      <div className="text-center" style={{ padding: "60px 16px" }}>
        <i className="fas fa-spinner fa-pulse fa-2x" style={{ color: "#6c757d" }} />
      </div>
    );
  }

  if (invalidMessage) {
    return <InvalidLink message={invalidMessage} />;
  }

  const shell = (children) => (
    <div style={{ maxWidth: "480px", margin: "0 auto", padding: "24px 16px 48px" }}>
      <div className="text-center m-b-20">
        <h4 style={{ fontWeight: 600, marginBottom: "4px" }}>
          {t("paylink.title", "Pay Your Rent")}
        </h4>
        <p style={{ color: "#6c757d", fontSize: "13px", margin: 0 }}>
          {t("paylink.subtitle", "Ania Management")}
        </p>
      </div>
      <div className="card">
        <div className="card-body">{children}</div>
      </div>
    </div>
  );

  if (approved) {
    return shell(
      <div className="text-center">
        <i className="fa fa-circle-check" style={{ fontSize: "36px", color: "#00acac" }} />
        <h5 style={{ marginTop: "14px", fontWeight: 600 }}>
          {t("paylink.approved-title", "Payment Received")}
        </h5>
        <p style={{ color: "#6c757d", fontSize: "14px", marginBottom: "6px" }}>
          {t("paylink.approved-body", "Thank you. A receipt is on its way to your email.")}
        </p>
        {approved.paymentZzID && (
          <p style={{ fontSize: "12px", color: "#6c757d", marginBottom: 0 }}>
            {t("paylink.confirmation", "Confirmation")}:{" "}
            <code style={{ fontSize: "12px" }}>{approved.paymentZzID}</code>
          </p>
        )}
        <p style={{ fontSize: "12px", color: "#6c757d", marginTop: "14px" }}>
          {t("paylink.link-spent", "This payment link has now been used and will not open again.")}
        </p>
      </div>
    );
  }

  if (unresolved) {
    return shell(
      <div className="text-center">
        <i className="fa fa-triangle-exclamation" style={{ fontSize: "32px", color: "#f59c1a" }} />
        <h5 style={{ marginTop: "14px", fontWeight: 600 }}>
          {t("paylink.unresolved-title", "We could not confirm your payment")}
        </h5>
        <p style={{ color: "#6c757d", fontSize: "14px" }}>
          {t(
            "paylink.unresolved-body",
            "Your payment may or may not have gone through. Please do not try again — call our office and we will check for you straight away."
          )}
        </p>
        {OFFICE_PHONE && (
          <p style={{ fontSize: "15px", fontWeight: 600 }}>
            <a href={OFFICE_PHONE_DIAL}>{OFFICE_PHONE}</a>
          </p>
        )}
      </div>
    );
  }

  if (locked) {
    return shell(
      <div className="text-center">
        <i className="fa fa-lock" style={{ fontSize: "32px", color: "#6c757d" }} />
        <h5 style={{ marginTop: "14px", fontWeight: 600 }}>
          {t("paylink.locked-title", "This link is now closed")}
        </h5>
        <p style={{ color: "#6c757d", fontSize: "14px" }}>
          {t(
            "paylink.locked-body",
            "There were too many declined attempts, so we have closed this link to keep your account safe. Please call our office and we'll take your payment over the phone."
          )}
        </p>
        {OFFICE_PHONE && (
          <p style={{ fontSize: "15px", fontWeight: 600 }}>
            <a href={OFFICE_PHONE_DIAL}>{OFFICE_PHONE}</a>
          </p>
        )}
      </div>
    );
  }

  const expires = linkContext?.expiresAt ? moment(linkContext.expiresAt) : null;
  const attemptsLeft = decline
    ? decline.attemptsRemaining
    : linkContext?.attemptsRemaining;

  return shell(
    <>
      {decline && (
        <div className="alert alert-danger" style={{ fontSize: "13px" }}>
          <strong style={{ display: "block", marginBottom: "4px" }}>
            {t("paylink.declined-title", "That payment was declined")}
          </strong>
          {decline.text && <div style={{ marginBottom: "6px" }}>{decline.text}</div>}
          <div>
            {t(
              "paylink.declined-help",
              "Nothing has been charged. You can try a different card or account below — the rest of your details have been kept."
            )}
          </div>
          {typeof attemptsLeft === "number" && (
            <div style={{ marginTop: "6px", fontWeight: 600 }}>
              {attemptsLeft === 1
                ? t("paylink.attempts-one", "You have 1 attempt left before this link closes.")
                : t("paylink.attempts-many", {
                    count: attemptsLeft,
                    defaultValue: `You have ${attemptsLeft} attempts left before this link closes.`,
                  })}
            </div>
          )}
        </div>
      )}

      <PayByLinkForm
        suggestedAmount={linkContext?.suggestedAmount || ""}
        suggestedFirstName={linkContext?.suggestedFirstName || ""}
        suggestedLastName={linkContext?.suggestedLastName || ""}
        feeRatePercent={linkContext?.feeRatePercent}
        submitting={submitting}
        secretsResetSignal={secretsResetSignal}
        onSubmit={handleSubmit}
      />

      {expires && expires.isValid() && (
        <p
          style={{
            fontSize: "11px",
            color: "#6c757d",
            textAlign: "center",
            marginTop: "14px",
            marginBottom: 0,
          }}
        >
          {t("paylink.expires", "This link expires")} {expires.format("MMMM D, YYYY")}
        </p>
      )}
    </>
  );
};

const PayByLinkPage = () => (
  <WrapperComponent>
    <PayByLink />
  </WrapperComponent>
);

export default PayByLinkPage;
